import React from "react";
import {
  SparklesIcon,
  InboxIcon,
  LockClosedIcon,
} from "@heroicons/react/24/outline";
import { EmptyState as UiEmptyState } from "../../../ui/EmptyState";

interface FailureClustersEmptyStateProps {
  /**
   * `not-enabled` — clustering is not enabled for the customer
   * (`clustering_enabled == false`, #1257). `cold-start` — the domain has
   * clustering enabled but no discovery has run yet
   * (`last_discovery_at == null`). `no-data` — discovery has run but the
   * selected window has no failures.
   */
  variant: "not-enabled" | "cold-start" | "no-data";
}

/**
 * The no-content states for the Failure Clusters page (design doc 015 §6).
 */
export function FailureClustersEmptyState({
  variant,
}: FailureClustersEmptyStateProps) {
  if (variant === "not-enabled") {
    return (
      <UiEmptyState
        icon={LockClosedIcon}
        size="lg"
        message="Clustering isn't enabled for your account"
        description="Failure-mode clustering automatically groups your failing traces into named, trackable modes. Please contact support to enable clustering for your organisation."
      />
    );
  }
  if (variant === "cold-start") {
    return (
      <UiEmptyState
        icon={SparklesIcon}
        size="lg"
        message="Discovering your failure modes…"
        description="We're judging your recent traces and grouping the failures into named modes. Your first clusters appear after the next daily discovery run."
      />
    );
  }
  return (
    <UiEmptyState
      icon={InboxIcon}
      size="lg"
      message="No failures in this window"
      description="No failing traces were recorded in the selected window. Try the weekly view for a longer window."
    />
  );
}
